import { useState, useRef, useEffect } from "react";
import { X, Plus, Trash2 } from "lucide-react";
import { apiClient, getCachedUser } from "@/lib/api";
import { buildVerifyBody, emptyForm } from "@/lib/verifyBody";
import type { VerifyFormState } from "@/lib/verifyBody";
import type { AssessmentAction } from "@/lib/types";

const VERDICTS = [
  "Fit for service",
  "Fit with conditions",
  "Not fit for service",
  "Further evaluation required",
];

export function VerifyForm({ assetId, onClose, onVerified, reverify }: {
  assetId: number;
  onClose: () => void;
  onVerified: () => void | Promise<void>;
  reverify: boolean;
}): JSX.Element {
  const user = getCachedUser();
  const [f, setF] = useState<VerifyFormState>(() => ({ ...emptyForm(), certRef: user?.cert_no ?? "" }));
  const [detail, setDetail] = useState(false);
  const [other, setOther] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const idRef = useRef(0);
  const mkId = () => String(idRef.current++);
  const [rows, setRows] = useState<Array<AssessmentAction & { _id: string }>>([]);
  const set = (patch: Partial<VerifyFormState>) => setF((prev) => ({ ...prev, ...patch }));

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape" && !busy) onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [busy, onClose]);

  function setAction(id: string, patch: Partial<AssessmentAction>) {
    setRows((prev) => prev.map((r) => (r._id === id ? { ...r, ...patch } : r)));
  }
  function addAction() {
    setRows((prev) => [...prev, { _id: mkId(), action: "", priority: "routine", due: "" }]);
  }
  function removeAction(id: string) {
    setRows((prev) => prev.filter((r) => r._id !== id));
  }

  async function submit() {
    setBusy(true);
    setError("");
    try {
      await apiClient.verifyReview(assetId, buildVerifyBody({ ...f, actions: rows.map(({ _id, ...a }) => a) }));
      await onVerified();
    } catch {
      setError("Sign-off failed. Check your connection and try again.");
    } finally {
      setBusy(false);
    }
  }

  const canSubmit = f.verdict.trim() !== "" && !busy;
  const isCustomVerdict = other || (f.verdict !== "" && !VERDICTS.includes(f.verdict));
  const lbl = "font-mono uppercase block mb-1";
  const lblStyle = { fontSize: 9, letterSpacing: ".12em", color: "var(--ink-4)" } as const;
  const field = "w-full px-2 py-1.5 font-sans";
  const fieldStyle = { fontSize: 13, border: "1px solid var(--hairline)", background: "var(--paper)" } as const;

  return (
    <div className="fixed inset-0 z-50 flex justify-end" role="dialog" aria-modal="true" aria-label="Engineer sign-off">
      <div className="flex-1" style={{ background: "rgba(0,0,0,.35)" }} onClick={busy ? undefined : onClose} />
      <div className="h-full overflow-y-auto" style={{ width: 440, maxWidth: "92vw", background: "var(--paper-2)", borderLeft: "1px solid var(--hairline)" }}>
        <div className="px-4 py-3 flex items-center justify-between" style={{ borderBottom: "1px solid var(--hairline)" }}>
          <div className="font-mono uppercase" style={{ fontSize: 11, letterSpacing: ".14em", color: "var(--ink-1)", fontWeight: 700 }}>
            {reverify ? "Re-verify · new cycle" : "Engineer sign-off"}
          </div>
          <button onClick={onClose} aria-label="Close" disabled={busy}><X size={16} /></button>
        </div>

        <div className="p-4 space-y-4">
          {error && (
            <div role="alert" className="px-3 py-2 font-mono" style={{ fontSize: 11, color: "var(--err)", border: "1px solid #B4231844", background: "#B423180a" }}>
              {error}
            </div>
          )}

          <div>
            <label htmlFor="vf-verdict" className={lbl} style={lblStyle}>Verdict</label>
            <select id="vf-verdict" aria-label="Verdict" className={field} style={fieldStyle}
                    value={isCustomVerdict ? "__other__" : f.verdict}
                    onChange={(e) => {
                      if (e.target.value === "__other__") { setOther(true); set({ verdict: "" }); }
                      else { setOther(false); set({ verdict: e.target.value }); }
                    }}>
              <option value="">— select —</option>
              {VERDICTS.map((v) => <option key={v} value={v}>{v}</option>)}
              <option value="__other__">Other (specify)…</option>
            </select>
            {isCustomVerdict && (
              <input aria-label="Custom verdict" className={`${field} mt-1`} style={fieldStyle} maxLength={64}
                     value={f.verdict} onChange={(e) => set({ verdict: e.target.value })}
                     placeholder="custom verdict" />
            )}
          </div>

          <div>
            <label htmlFor="vf-cert" className={lbl} style={lblStyle}>Certification (cert ref)</label>
            <input id="vf-cert" aria-label="Certification" className={field} style={fieldStyle}
                   value={f.certRef} onChange={(e) => set({ certRef: e.target.value })}
                   placeholder="defaults to your certification on file" />
          </div>

          <div>
            <label htmlFor="vf-notes" className={lbl} style={lblStyle}>Notes</label>
            <textarea id="vf-notes" aria-label="Notes" className={field} style={fieldStyle} rows={3}
                      value={f.notes} onChange={(e) => set({ notes: e.target.value })} />
          </div>

          <button type="button" onClick={() => setDetail((d) => !d)}
                  className="font-mono uppercase" style={{ fontSize: 10, letterSpacing: ".1em", color: "var(--accent-deep)" }}>
            {detail ? "▾ Detailed assessment" : "▸ Detailed assessment (optional)"}
          </button>

          {detail && (
            <div className="space-y-3">
              {([
                ["Damage mechanisms", "damageMechanisms"],
                ["Summary", "summary"],
                ["Corrosion basis", "corrosionBasis"],
                ["Interval basis", "intervalBasis"],
                ["Confidence", "confidence"],
              ] as const).map(([label, key]) => (
                <div key={key}>
                  <label htmlFor={`vf-${key}`} className={lbl} style={lblStyle}>{label}</label>
                  <textarea id={`vf-${key}`} aria-label={label} className={field} style={fieldStyle} rows={2}
                            value={f[key] as string} onChange={(e) => set({ [key]: e.target.value } as Partial<VerifyFormState>)} />
                </div>
              ))}

              <div>
                <label className={lbl} style={lblStyle}>Fitness-for-service</label>
                <label className="flex items-center gap-2 font-sans" style={{ fontSize: 12.5 }}>
                  <input type="checkbox" checked={f.ffsRequired} onChange={(e) => set({ ffsRequired: e.target.checked })} />
                  FFS assessment required
                </label>
                {f.ffsRequired && (
                  <textarea aria-label="FFS note" className={`${field} mt-1`} style={fieldStyle} rows={2}
                            value={f.ffsNote} onChange={(e) => set({ ffsNote: e.target.value })} placeholder="FFS note" />
                )}
              </div>

              <div>
                <label className={lbl} style={lblStyle}>Action items</label>
                {rows.map((r, i) => (
                  <div key={r._id} className="flex items-center gap-1 mb-1">
                    <input aria-label={`Action ${i + 1}`} className={field} style={fieldStyle} value={r.action}
                           onChange={(e) => setAction(r._id, { action: e.target.value })} placeholder="action" />
                    <select aria-label={`Priority ${i + 1}`} style={fieldStyle} value={r.priority}
                            onChange={(e) => setAction(r._id, { priority: e.target.value })}>
                      <option value="routine">routine</option>
                      <option value="priority">priority</option>
                      <option value="urgent">urgent</option>
                    </select>
                    <input aria-label={`Due ${i + 1}`} style={{ ...fieldStyle, width: 90 }} className={field} value={r.due}
                           onChange={(e) => setAction(r._id, { due: e.target.value })} placeholder="due" />
                    <button onClick={() => removeAction(r._id)} aria-label={`Remove action ${i + 1}`}><Trash2 size={14} /></button>
                  </div>
                ))}
                <button onClick={addAction} className="flex items-center gap-1 font-mono uppercase mt-1"
                        style={{ fontSize: 10, letterSpacing: ".1em", color: "var(--accent-deep)" }}>
                  <Plus size={12} /> Add action
                </button>
              </div>
            </div>
          )}

          <div className="flex items-center gap-2 pt-2" style={{ borderTop: "1px solid var(--hairline)" }}>
            <button onClick={submit} disabled={!canSubmit}
                    className="px-4 py-2 font-mono uppercase"
                    style={{ fontSize: 10, letterSpacing: ".12em", color: "#fff", background: canSubmit ? "var(--ok)" : "var(--ink-4)", cursor: canSubmit ? "pointer" : "not-allowed" }}>
              {busy ? "Signing…" : "Sign off"}
            </button>
            <button onClick={onClose} disabled={busy} className="px-3 py-2 font-mono uppercase" style={{ fontSize: 10, letterSpacing: ".1em", color: "var(--ink-3)" }}>
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
